/**
 * LatentCollectionPage
 * Defaulted credits portfolio — KPI summary, recovery analysis, and detailed credits table
 */

import {
  FileText,
  Banknote,
  CircleDollarSign,
  Clock,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatCard } from '@/components/data-display/StatCard';
import { ROUTES } from '@/constants';

import { useLatentCollection } from '@/features/collection/hooks';
import { formatCurrencyMXN, DEFAULTED_STATUS_LABEL, DEFAULTED_STATUS_VARIANT } from '@/features/collection/types';
import { formatNumber } from '@/utils/formatters';
import type { DefaultedCredit } from '@/types/portfolio.types';

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Latente', path: ROUTES.LATENT_COLLECTION },
];

function formatShortDate(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

// ─── Status Distribution ────────────────────────────────────────────────────

function StatusDistribution({ credits }: { credits: DefaultedCredit[] }) {
  const statusCounts: Record<string, { count: number; amount: number }> = {};
  for (const c of credits) {
    const entry = statusCounts[c.status] ?? { count: 0, amount: 0 };
    entry.count += 1;
    entry.amount += c.outstanding_balance_mxn;
    statusCounts[c.status] = entry;
  }

  const statuses = Object.entries(statusCounts).sort((a, b) => b[1].count - a[1].count);

  if (statuses.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">Sin datos</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="pb-2 font-medium">Estado</th>
            <th className="pb-2 text-right font-medium">Creditos</th>
            <th className="pb-2 text-right font-medium">Pendiente</th>
          </tr>
        </thead>
        <tbody>
          {statuses.map(([status, data]) => (
            <tr key={status} className="border-b border-border/50">
              <td className="py-2">
                <Badge
                  variant={(DEFAULTED_STATUS_VARIANT[status] ?? 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info'}
                >
                  {DEFAULTED_STATUS_LABEL[status] ?? status}
                </Badge>
              </td>
              <td className="py-2 text-right tabular-nums text-foreground">{data.count}</td>
              <td className="py-2 text-right tabular-nums text-foreground">{formatCurrencyMXN(data.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Aging Distribution ─────────────────────────────────────────────────────

function AgingDistribution({ credits }: { credits: DefaultedCredit[] }) {
  const buckets = [
    { label: '8-12 semanas', min: 8, max: 12 },
    { label: '13-20 semanas', min: 13, max: 20 },
    { label: '21-26 semanas', min: 21, max: 26 },
    { label: '26+ semanas', min: 27, max: Infinity },
  ];

  const distribution = buckets.map((bucket) => {
    const matching = credits.filter((c) => c.weeks_without_payment >= bucket.min && c.weeks_without_payment <= bucket.max);
    return {
      label: bucket.label,
      count: matching.length,
      amount: matching.reduce((s, c) => s + c.outstanding_balance_mxn, 0),
    };
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="pb-2 font-medium">Antiguedad</th>
            <th className="pb-2 text-right font-medium">Creditos</th>
            <th className="pb-2 text-right font-medium">Pendiente</th>
          </tr>
        </thead>
        <tbody>
          {distribution.map((row) => (
            <tr key={row.label} className="border-b border-border/50">
              <td className="py-2 font-medium text-foreground">{row.label}</td>
              <td className="py-2 text-right tabular-nums text-foreground">{row.count}</td>
              <td className="py-2 text-right tabular-nums text-foreground">{formatCurrencyMXN(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Credits Detail Table ───────────────────────────────────────────────────

function CreditsTable({ credits }: { credits: DefaultedCredit[] }) {
  if (credits.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin creditos en cobranza latente</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="px-4 py-2 font-medium">Inicio Mora</th>
            <th className="px-4 py-2 font-medium">ID Credito</th>
            <th className="px-4 py-2 text-right font-medium">Monto Original</th>
            <th className="px-4 py-2 text-right font-medium">Cobrado</th>
            <th className="px-4 py-2 text-right font-medium">Pendiente</th>
            <th className="px-4 py-2 text-right font-medium">Recuperacion</th>
            <th className="px-4 py-2 text-right font-medium">Semanas Mora</th>
            <th className="px-4 py-2 font-medium">Estado</th>
            <th className="px-4 py-2 font-medium">Ultima Actividad</th>
          </tr>
        </thead>
        <tbody>
          {credits.map((credit) => {
            const recoveryPct = credit.amount_mxn > 0
              ? (credit.total_collected_mxn / credit.amount_mxn) * 100
              : 0;
            return (
              <tr key={credit.credit_id} className="border-b border-border/50 transition-colors hover:bg-muted/30">
                <td className="px-4 py-2 text-xs text-foreground">
                  {formatShortDate(credit.delinquency_start_date)}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-foreground" title={credit.credit_id}>
                  {credit.credit_id.slice(0, 8)}...
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-foreground">
                  {formatCurrencyMXN(credit.amount_mxn)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-foreground">
                  {formatCurrencyMXN(credit.total_collected_mxn)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-foreground">
                  {formatCurrencyMXN(credit.outstanding_balance_mxn)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <span className={recoveryPct >= 50 ? 'text-green-600' : recoveryPct >= 25 ? 'text-amber-600' : 'text-red-600'}>
                    {recoveryPct.toFixed(1)}%
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-red-600">
                  {credit.weeks_without_payment}
                </td>
                <td className="px-4 py-2">
                  <Badge
                    variant={(DEFAULTED_STATUS_VARIANT[credit.status] ?? 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info'}
                  >
                    {DEFAULTED_STATUS_LABEL[credit.status] ?? credit.status}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">
                  {formatShortDate(credit.updated_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function LatentSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={`stat-skeleton-${i.toString()}`} className="rounded-lg border border-border bg-card p-5">
            <Skeleton variant="text" width="60%" height={14} />
            <div className="mt-3"><Skeleton variant="text" width="50%" height={28} /></div>
          </div>
        ))}
      </div>
      <Skeleton variant="rectangular" height={200} />
    </div>
  );
}

// ─── Error State ────────────────────────────────────────────────────────────

function LatentError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">Error al cargar cobranza latente</h3>
      <p className="mt-1 text-sm text-muted-foreground">Hubo un error al obtener los datos.</p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} /> Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function LatentCollectionPage() {
  const { portfolio, isLoading, isError, refetch } = useLatentCollection();

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Latente" helpKey="latent-collection" subtitle="Créditos defaulteados y recuperación" breadcrumbs={BREADCRUMBS} />
        <LatentSkeleton />
      </div>
    );
  }

  if (isError || !portfolio) {
    return (
      <div>
        <PageHeader title="Latente" helpKey="latent-collection" subtitle="Créditos defaulteados y recuperación" breadcrumbs={BREADCRUMBS} />
        <LatentError onRetry={refetch} />
      </div>
    );
  }

  const recoveryRate = portfolio.total_debt_original_mxn > 0
    ? (portfolio.total_collected_mxn / portfolio.total_debt_original_mxn) * 100
    : 0;

  const credits = portfolio.credits ?? [];

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-4 text-sm">
        <Link
          to={ROUTES.PORTFOLIO_DEFAULTED}
          className="inline-block text-primary hover:underline"
        >
          → Ver resumen financiero de cartera en default
        </Link>
        <Link
          to={ROUTES.LIENS_LIST}
          className="inline-block text-primary hover:underline"
        >
          → Ver wallet liens activos
        </Link>
      </div>
      <PageHeader
        title="Latente" helpKey="latent-collection"
        subtitle="Cobro automático sobre depósitos detectados (próximamente con lien activo)"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">

            <Button onClick={refetch}>
              <RefreshCw size={16} /> Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* ── Row 1: KPIs ──────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            title="Creditos"
            value={formatNumber(portfolio.total_defaulted)}
            icon={FileText}
          />
          <StatCard
            title="Deuda Original"
            value={formatCurrencyMXN(portfolio.total_debt_original_mxn)}
            icon={Banknote}
          />
          <StatCard
            title="Cobrado"
            value={formatCurrencyMXN(portfolio.total_collected_mxn)}
            icon={CircleDollarSign}
          />
          <StatCard
            title="Pendiente"
            value={formatCurrencyMXN(portfolio.total_pending_mxn)}
            icon={Clock}
          />
          <StatCard
            title="Tasa Recuperacion"
            value={`${recoveryRate.toFixed(1)}%`}
            icon={TrendingUp}
          />
        </div>

        {/* ── Row 2: Analysis ──────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Por Estado" description="Distribucion de creditos latentes por estado">
            <StatusDistribution credits={credits} />
          </Card>
          <Card title="Por Antigüedad" description="Semanas sin pago">
            <AgingDistribution credits={credits} />
          </Card>
        </div>

        {/* ── Row 3: Detail Table ──────────────────────────────────── */}
        <Card title="Detalle de Creditos" description={`${credits.length} creditos en cobranza latente`}>
          <CreditsTable credits={credits} />
        </Card>
      </div>
    </div>
  );
}
